import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { pts, tzs, fmtDate, fmtDateTime, initials } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function BizCustomers() {
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/business/customers").then((r) => setList(r.data)).catch(() => setList([])); }, []);

  const open = async (id) => {
    try { const { data } = await api.get(`/business/customers/${id}`); setDetail(data); }
    catch { toast.error("Could not load customer"); }
  };

  if (!list) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <h1 className="font-display font-bold text-3xl">Customers</h1>
      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-wider border-b border-black/8">
            <tr><th className="text-left p-4">Customer</th><th className="text-right p-4">Points</th><th className="text-right p-4 hidden sm:table-cell">Visits</th><th className="text-right p-4 hidden md:table-cell">Last visit</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No customers yet.</td></tr>}
            {list.map((c) => (
              <tr key={c.customer_id} data-testid="biz-customer-row" onClick={() => open(c.customer_id)} className="border-b border-black/5 hover:bg-black/5 cursor-pointer">
                <td className="p-4 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-xs">{initials(c.name)}</div>
                  {c.name}
                </td>
                <td className="p-4 text-right font-mono text-primary">{pts(c.points)}</td>
                <td className="p-4 text-right hidden sm:table-cell">{c.visits}</td>
                <td className="p-4 text-right hidden md:table-cell text-muted-foreground">{c.last_visit ? fmtDate(c.last_visit) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-black/10 max-w-lg">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display">{detail.name}</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground">Member since {fmtDate(detail.member_since)}</p>
              <div className="grid grid-cols-2 gap-3 my-2">
                <div className="card-surface p-3"><p className="font-display font-bold text-xl text-primary">{pts(detail.points)}</p><p className="text-[10px] text-muted-foreground">Current points</p></div>
                <div className="card-surface p-3"><p className="font-display font-bold text-xl">{detail.visits}</p><p className="text-[10px] text-muted-foreground">Total visits</p></div>
                <div className="card-surface p-3"><p className="font-display font-bold text-xl">{tzs(detail.total_spend)}</p><p className="text-[10px] text-muted-foreground">Spend captured</p></div>
                <div className="card-surface p-3"><p className="font-display font-bold text-xl">{pts(detail.earned)} / {pts(detail.redeemed)}</p><p className="text-[10px] text-muted-foreground">Earned / Redeemed</p></div>
              </div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mt-2">Transaction history</p>
              <div className="max-h-56 overflow-y-auto space-y-1.5">
                {detail.transactions.map((t) => (
                  <div key={t.id} className="flex justify-between items-center text-sm border-b border-black/5 py-1.5">
                    <span className="capitalize">{t.type === "redemption" ? t.reward_name : t.type} <span className="text-muted-foreground text-xs">{fmtDateTime(t.created_at)}</span></span>
                    <span className={t.type === "redemption" ? "text-destructive" : "text-primary"}>{t.type === "redemption" ? `-${pts(t.points_spent)}` : `+${pts(t.points_awarded)}`}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
